import store from "state/RootReducer";
import { RestEnds } from "../Constants";
import Toast from "../helpers/Toast";
import axios from "axios";
import { AuthActions } from "state/auth/Action";

const REQUEST_BLOCK_CONFIG = {
  maxRetries: 3,
  blockDuration: 1 * 3 * 1000,
};

const endpointFailures = new Map();
const blockedEndpoints = new Map();

const AxiosManager = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

const getAccessToken = () => (store.getState() as any).authState.accessToken;
const getRefreshToken = () => (store.getState() as any).authState.refreshToken;

const isEndpointBlocked = (url) => {
  const blockInfo = blockedEndpoints.get(url);
  if (!blockInfo) return false;

  const now = Date.now();
  if (now > blockInfo.blockedUntil) {
    blockedEndpoints.delete(url);
    endpointFailures.delete(url);
    return false;
  }

  return true;
};

const recordFailure = (url) => {
  const failures = endpointFailures.get(url) || 0;
  const newFailureCount = failures + 1;

  endpointFailures.set(url, newFailureCount);

  if (newFailureCount >= REQUEST_BLOCK_CONFIG.maxRetries) {
    const blockedUntil = Date.now() + REQUEST_BLOCK_CONFIG.blockDuration;
    blockedEndpoints.set(url, {
      blockedUntil,
      failureCount: newFailureCount,
    });

    setTimeout(() => {
      Toast.showErrorMessage(
        `Exceeded the maximum number of requests. Please wait 3 seconds`,
        {
          autoClose: 8000,
        }
      );
    }, 1000);
  }
};

const resetFailureCount = (url) => {
  endpointFailures.delete(url);
  blockedEndpoints.delete(url);
};

AxiosManager.interceptors.request.use(
  (configInfo) => {
    const config = configInfo;

    if (isEndpointBlocked(config.url)) {
      const blockInfo = blockedEndpoints.get(config.url);
      const remainingTime = Math.ceil(
        (blockInfo.blockedUntil - Date.now()) / 1000
      );

      return Promise.reject({
        isBlocked: true,
        message: `Endpoint blocked. Try again in ${remainingTime} seconds.`,
        config: config,
      });
    }

    if (
      config.headers["append-auth-header"] === "true" &&
      getAccessToken() !== ""
    ) {
      config.headers["x-auth-token"] = `bearer ${getAccessToken()}`;
    }

    config.headers["Cache-Control"] = "no-cache";
    config.headers["Pragma"] = "no-cache";
    config.headers["Expires"] = "0";

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

AxiosManager.interceptors.response.use(
  (response) => {
    if (response.config && response.config.url) {
      resetFailureCount(response.config.url);
    }
    return response;
  },
  (error) => {
    if (error.isBlocked) {
      return Promise.reject(error);
    }

    const { config, response } = error;

    if (config && config.url) {
      const shouldBlock =
        !response ||
        response.status >= 500 ||
        response.status === 429 ||
        response.status === 408 ||
        response.status === 400 ||
        response.status === 409 ||
        !navigator.onLine;

      if (shouldBlock) {
        recordFailure(config.url);
      }
    }

    if (response) {
      const { status, data } = response;

      if (status === 401) {
        store.dispatch(AuthActions.resetStore({}));
      }

      // if (status === 429) {
      //   const { msg } = data;
      //   if (data && msg) {
      //     Toast.showErrorMessage(msg);
      //   }
      // }
    }

    return Promise.reject(error);
  }
);

export const unblockEndpoint = (url) => {
  blockedEndpoints.delete(url);
  endpointFailures.delete(url);
  console.log(`Endpoint ${url} manually unblocked`);
};

export const getBlockingStatus = () => {
  const status = {};
  blockedEndpoints.forEach((value, key) => {
    const remainingTime = Math.max(0, value.blockedUntil - Date.now());
    status[key] = {
      blockedUntil: new Date(value.blockedUntil).toISOString(),
      remainingTimeMs: remainingTime,
      remainingTimeSeconds: Math.ceil(remainingTime / 1000),
      failureCount: value.failureCount,
    };
  });
  return status;
};

export default AxiosManager;
